import { Button, ButtonVariant, Input, Select } from "shared/components";
import { CombinedIncomeLayout } from "../components/Layout";
import { toast } from "react-toastify";
import { CombinedIncomeSubStep } from "../../../constants";
import { useFormTyped } from "shared/hooks/use-form-typed";
import { object, string } from "yup";
import { useCombinedIncome } from "../context";
import { getErrorMessage } from "utils/error";
import { IncomeType } from "shared/types";
import { CreateIncomePayload } from "../types";

type PreTaxIncomeFormData = {
  amount: string;
  pay_period: string;
};

const validationSchema = object({
  amount: string().required("Pre-tax income is required"),
  pay_period: string().required("Pay period is required")
});

const payPeriodOptions = [
  { label: "Monthly", value: "monthly" },
  { label: "Annually", value: "annually" },
  { label: "Biweekly", value: "bi_weekly" },
  { label: "Weekly", value: "weekly" }
];

export const PreTaxIncomeStep = () => {
  const {
    updateStepMutation,
    createIncomeMutation,
    getNextStep,
    stepData,
    refetchVerification
  } = useCombinedIncome();

  const { values, register, onSubmit } = useFormTyped<PreTaxIncomeFormData>(
    {
      amount: stepData.amount || "",
      pay_period: stepData.pay_period || "monthly"
    },
    validationSchema
  );

  const handleSubmit = async (formValues: PreTaxIncomeFormData) => {
    try {
      const payload: CreateIncomePayload = {
        type: stepData.currentIncomeType as IncomeType,
        start_date: stepData.start_date,
        pay_period: formValues.pay_period,
        amount: {
          cents: Number(formValues.amount) * 100 || 0,
          currency: "USD"
        },
        additional_data: {}
      };

      if (stepData.company_name)
        payload.additional_data!.company_name = stepData.company_name;
      if (stepData.job_title)
        payload.additional_data!.job_title = stepData.job_title;
      if (stepData.supervisor)
        payload.additional_data!.supervisor = {
          first_name: stepData.supervisor.first_name,
          last_name: stepData.supervisor.last_name,
          email: stepData.supervisor.contact_email,
          phone: stepData.supervisor.contact_phone
        };
      if (stepData.description)
        payload.additional_data!.description = stepData.description;
      if (stepData.paid_from)
        payload.additional_data!.paid_from = stepData.paid_from;

      const createdIncome = await createIncomeMutation.mutateAsync(payload);

      const nextStep = getNextStep(CombinedIncomeSubStep.PreTaxIncome, {
        incomeType: stepData.currentIncomeType
      });

      if (!nextStep) return;

      await updateStepMutation.mutateAsync({
        lastStep: nextStep,
        currentIncomeId: createdIncome.id,
        pay_period: formValues.pay_period,
        amount: formValues.amount
      });

      refetchVerification();
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to save income details");
    }
  };

  const isValid = values.amount && values.pay_period;
  const isLoading =
    createIncomeMutation.isPending || updateStepMutation.isPending;

  return (
    <form onSubmit={onSubmit({ onSuccess: handleSubmit })}>
      <CombinedIncomeLayout
        title="Add pre-tax income"
        footer={
          <Button
            className="w-[132px]"
            type="submit"
            loading={isLoading}
            variant={ButtonVariant.PRIMARY}
            disabled={!isValid}
          >
            Continue
          </Button>
        }
      >
        <p className="mb-1 text-content-700 text-sm font-medium">
          Pre-tax income
        </p>
        <div className="flex gap-2">
          <div className="w-full">
            <Select
              nativeMobileSelect
              id="pay_period"
              {...register("pay_period")}
              options={payPeriodOptions}
              placeholder="Month"
              required
              legacyErrorMargins={false}
            />
          </div>
          <div className="w-full">
            <Input
              id="amount"
              type="money"
              placeholder="60,000"
              {...register("amount")}
              required
              legacyErrorMargins={false}
            />
          </div>
        </div>
      </CombinedIncomeLayout>
    </form>
  );
};
